// src/pages/EmployeeQuery.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    FiPlus, FiSearch, FiFilter, FiClock, FiCheckCircle, FiAlertCircle,
    FiRefreshCw, FiX, FiSend, FiUser, FiCalendar, FiMessageSquare,
    FiPaperclip, FiChevronDown, FiChevronUp, FiEdit2, FiTrash2,
    FiEye, FiChevronLeft, FiChevronRight, FiDollarSign, FiFileText,
    FiFolder, FiTag, FiUserCheck, FiBriefcase, FiInfo, FiMail,
    FiPhone, FiMapPin, FiBook, FiHash, FiUsers, FiBell, FiStar,
    FiTrendingUp, FiBarChart2, FiSave
} from 'react-icons/fi';
import { useQueryContext } from '../context/QueryContext';
import { useAuth } from '../context/AuthContext';
import { format, formatDistanceToNow, formatDistance } from 'date-fns';

const EmployeeQuery = () => {
    const {
        queries,
        myQueries,
        selectedQuery,
        comments,
        loading,
        error,
        dashboardStats,
        recentQueries,
        pagination,
        myQueryStats,
        employeeGroups,
        employeePagination,
        fetchQueries,
        fetchMyQueries,
        fetchQueryDetails,
        fetchComments,
        fetchDashboardStats,
        fetchRecentQueries,
        fetchMyQueryStats,
        fetchEmployeeGroups,
        createQuery,
        updateQuery,
        deleteQuery,
        addComment,
        deleteComment,
        assignQuery,
        resolveQuery,
        closeQuery,
        reopenQuery,
        refreshAllData,
        getStatusBadge,
        getPriorityBadge,
        getCategoryLabel,
        clearError
    } = useQueryContext();

    const { isAdminOrHR, isEmployee, user } = useAuth();
    const [activeTab, setActiveTab] = useState(isAdminOrHR ? 'groups' : 'my');
    const [showNewQueryModal, setShowNewQueryModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedQueryId, setSelectedQueryId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPriority, setFilterPriority] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [expandedEmployees, setExpandedEmployees] = useState(new Set());
    const [expandedQueries, setExpandedQueries] = useState(new Set());
    // Keyed by queryId so each query's comment box has its own independent draft/attachment
    const [newCommentMap, setNewCommentMap] = useState({});
    const [commentAttachmentMap, setCommentAttachmentMap] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingQueryId, setEditingQueryId] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [showEditModal, setShowEditModal] = useState(false);
    const [highlightedEmployee, setHighlightedEmployee] = useState(null);
    const [animatedEmployees, setAnimatedEmployees] = useState(new Set());
    const [queryCommentsMap, setQueryCommentsMap] = useState({});
    const [loadingComments, setLoadingComments] = useState({});

    // Pagination state for all queries
    const [queryPage, setQueryPage] = useState(1);
    const [myQueryPage, setMyQueryPage] = useState(1);
    const searchTimeoutRef = useRef(null);
    const filterTimeoutRef = useRef(null);

    // Form state for new query
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: 'general',
        priority: 'medium',
        attachment: null,
        is_public: true
    });

    // Load data on mount
    useEffect(() => {
        refreshAllData();
    }, []);

    // Check for new queries and trigger animation
    useEffect(() => {
        if (employeeGroups.length > 0) {
            const newEmployees = employeeGroups.filter(
                emp => emp.latest_query &&
                    new Date(emp.latest_query.created_at) > new Date(Date.now() - 30000)
            );

            if (newEmployees.length > 0) {
                const newSet = new Set(animatedEmployees);
                newEmployees.forEach(emp => {
                    const key = `${emp.employee_id}-${emp.user_id}`;
                    newSet.add(key);
                    setHighlightedEmployee(key);
                });
                setAnimatedEmployees(newSet);

                setTimeout(() => {
                    setAnimatedEmployees(new Set());
                    setHighlightedEmployee(null);
                }, 5000);
            }
        }
    }, [employeeGroups]);

    // Load selected query details when query ID changes
    useEffect(() => {
        if (selectedQueryId) {
            fetchQueryDetails(selectedQueryId);
            fetchComments(selectedQueryId);
        }
    }, [selectedQueryId]);

    // Apply filters with debounce
    const applyFilters = useCallback(() => {
        const filters = {};
        if (filterStatus !== 'all') filters.status = filterStatus;
        if (filterPriority !== 'all') filters.priority = filterPriority;
        if (filterCategory !== 'all') filters.category = filterCategory;
        if (searchTerm) filters.search = searchTerm;

        console.log('Applying filters:', filters, 'Active tab:', activeTab);

        if (activeTab === 'groups' && isAdminOrHR) {
            fetchEmployeeGroups({ page: 1, pageSize: 5, filters });
        } else if (activeTab === 'all' && isAdminOrHR) {
            setQueryPage(1);
            fetchQueries({ page: 1, pageSize: pagination.pageSize || 10, filters });
        } else if (activeTab === 'my' && isEmployee) {
            // Client-side filtering - just refresh the current page
            setMyQueryPage(1);
            fetchMyQueries();
        }
    }, [filterStatus, filterPriority, filterCategory, searchTerm, activeTab, isAdminOrHR, isEmployee, fetchEmployeeGroups, fetchQueries, fetchMyQueries, pagination.pageSize]);

    // Debounced search
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout
        searchTimeoutRef.current = setTimeout(() => {
            applyFilters();
        }, 500);
    };

    const handleFilterChange = (type, value) => {
        // Clear previous filter timeout
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }

        switch (type) {
            case 'status':
                setFilterStatus(value);
                break;
            case 'priority':
                setFilterPriority(value);
                break;
            case 'category':
                setFilterCategory(value);
                break;
            default:
                break;
        }

        // Apply filters after state update
        filterTimeoutRef.current = setTimeout(() => {
            applyFilters();
        }, 100);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilterStatus('all');
        setFilterPriority('all');
        setFilterCategory('all');
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }
        applyFilters();
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);

        // Reset pages when switching tabs
        if (tab === 'groups' && isAdminOrHR) {
            const filters = {};
            if (filterStatus !== 'all') filters.status = filterStatus;
            if (filterPriority !== 'all') filters.priority = filterPriority;
            if (filterCategory !== 'all') filters.category = filterCategory;
            if (searchTerm) filters.search = searchTerm;
            fetchEmployeeGroups({ page: 1, pageSize: 5, filters });
        } else if (tab === 'all' && isAdminOrHR) {
            setQueryPage(1);
            const filters = {};
            if (filterStatus !== 'all') filters.status = filterStatus;
            if (filterPriority !== 'all') filters.priority = filterPriority;
            if (filterCategory !== 'all') filters.category = filterCategory;
            if (searchTerm) filters.search = searchTerm;
            fetchQueries({ page: 1, pageSize: pagination.pageSize || 10, filters });
        } else if (tab === 'my' && isEmployee) {
            setMyQueryPage(1);
            fetchMyQueries();
            fetchMyQueryStats();
        } else if (tab === 'stats' && isAdminOrHR) {
            fetchDashboardStats();
        }
    };

    const toggleExpandEmployee = (employeeKey) => {
        setExpandedEmployees(prev => {
            const newSet = new Set(prev);
            if (newSet.has(employeeKey)) {
                newSet.delete(employeeKey);
            } else {
                newSet.add(employeeKey);
            }
            return newSet;
        });
    };

    const toggleExpandQuery = (queryId) => {
        setExpandedQueries(prev => {
            const newSet = new Set(prev);
            if (newSet.has(queryId)) {
                newSet.delete(queryId);
            } else {
                newSet.add(queryId);
                // Load comments when expanding
                loadQueryComments(queryId);
            }
            return newSet;
        });
    };

    // Load comments for a specific query
    const loadQueryComments = async (queryId, force = false) => {
        if (!force && queryCommentsMap[queryId]) return; // Already loaded, skip unless forced

        setLoadingComments(prev => ({ ...prev, [queryId]: true }));
        try {
            const response = await fetchComments(queryId);
            if (response) {
                setQueryCommentsMap(prev => ({ ...prev, [queryId]: response }));
            }
        } catch (err) {
            console.error('Error loading comments:', err);
        } finally {
            setLoadingComments(prev => ({ ...prev, [queryId]: false }));
        }
    };

    // Refresh button handler - refreshAllData() only re-pulls the query lists/stats.
    // Comments for already-expanded sections (and an open detail modal) are cached
    // locally, so they need to be force-refetched too or they'll look "stuck".
    const handleRefresh = async () => {
        await refreshAllData();

        expandedQueries.forEach((queryId) => {
            loadQueryComments(queryId, true);
        });

        if (selectedQueryId) {
            fetchQueryDetails(selectedQueryId);
            fetchComments(selectedQueryId);
        }
    };

    const openQueryDetail = (queryId) => {
        setSelectedQueryId(queryId);
        setShowDetailModal(true);
        fetchQueryDetails(queryId);
        fetchComments(queryId);
    };

    const handleFormChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            setFormData(prev => ({ ...prev, [name]: files[0] }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitQuery = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await createQuery(formData);
            setShowNewQueryModal(false);
            setFormData({
                title: '',
                description: '',
                category: 'general',
                priority: 'medium',
                attachment: null,
                is_public: true
            });
            refreshAllData();
        } catch (err) {
            console.error('Submit error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateQuery = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await updateQuery(editingQueryId, editFormData);
            setShowEditModal(false);
            setEditingQueryId(null);
            refreshAllData();
        } catch (err) {
            console.error('Update error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteQuery = async (queryId) => {
        if (window.confirm('Are you sure you want to delete this query?')) {
            try {
                await deleteQuery(queryId);
                refreshAllData();
            } catch (err) {
                console.error('Delete error:', err);
            }
        }
    };

    const handleAddComment = async (queryId) => {
        const commentText = newCommentMap[queryId] || '';
        if (!commentText.trim()) return;

        const attachment = commentAttachmentMap[queryId] || null;

        setIsSubmitting(true);
        try {
            const data = {
                comment: commentText,
                is_internal: false,
                is_resolution: false
            };

            let response;
            if (attachment) {
                const formData = new FormData();
                formData.append('comment', commentText);
                formData.append('attachment', attachment);
                response = await addComment(queryId, formData);
            } else {
                response = await addComment(queryId, data);
            }

            setNewCommentMap(prev => ({ ...prev, [queryId]: '' }));
            setCommentAttachmentMap(prev => ({ ...prev, [queryId]: null }));

            // Refresh comments for this query
            const updatedComments = await fetchComments(queryId);
            if (updatedComments) {
                setQueryCommentsMap(prev => ({ ...prev, [queryId]: updatedComments }));
            }
            refreshAllData();
        } catch (err) {
            console.error('Add comment error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStatusUpdate = async (queryId, action, notes = '') => {
        try {
            switch (action) {
                case 'resolve':
                    await resolveQuery(queryId, notes);
                    break;
                case 'close':
                    await closeQuery(queryId, notes);
                    break;
                case 'reopen':
                    await reopenQuery(queryId);
                    break;
                default:
                    break;
            }
            refreshAllData();
            if (selectedQueryId) {
                fetchQueryDetails(selectedQueryId);
                fetchComments(selectedQueryId);
            }
        } catch (err) {
            console.error('Status update error:', err);
        }
    };

    const openEditModal = (query) => {
        setEditingQueryId(query.id);
        setEditFormData({
            title: query.title,
            description: query.description,
            category: query.category,
            priority: query.priority,
            status: query.status,
            resolution_notes: query.resolution_notes || ''
        });
        setShowEditModal(true);
    };

    const formatDateDisplay = (date) => {
        if (!date) return 'N/A';
        return format(new Date(date), 'dd MMM yyyy, hh:mm a');
    };

    const getStatusIcon = (status) => {
        const icons = {
            'pending': FiClock,
            'in_progress': FiRefreshCw,
            'resolved': FiCheckCircle,
            'closed': FiCheckCircle,
            'rejected': FiAlertCircle
        };
        return icons[status] || FiClock;
    };

    const getPriorityColor = (priority) => {
        const colors = {
            'low': 'text-blue-600 bg-blue-50 border-blue-200',
            'medium': 'text-yellow-600 bg-yellow-50 border-yellow-200',
            'high': 'text-orange-600 bg-orange-50 border-orange-200',
            'urgent': 'text-red-600 bg-red-50 border-red-200'
        };
        return colors[priority] || colors['medium'];
    };

    // Pagination handlers
    const handleEmployeePageChange = (newPage) => {
        const filters = {};
        if (filterStatus !== 'all') filters.status = filterStatus;
        if (filterPriority !== 'all') filters.priority = filterPriority;
        if (filterCategory !== 'all') filters.category = filterCategory;
        if (searchTerm) filters.search = searchTerm;

        fetchEmployeeGroups({ page: newPage, pageSize: 5, filters });
    };

    const handleQueryPageChange = (newPage) => {
        const filters = {};
        if (filterStatus !== 'all') filters.status = filterStatus;
        if (filterPriority !== 'all') filters.priority = filterPriority;
        if (filterCategory !== 'all') filters.category = filterCategory;
        if (searchTerm) filters.search = searchTerm;

        setQueryPage(newPage);
        fetchQueries({ page: newPage, pageSize: pagination.pageSize || 10, filters });
    };

    // Client-side filtering and pagination for my queries
    const getFilteredMyQueries = () => {
        let filtered = [...myQueries];

        // Apply search
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(q =>
                q.title?.toLowerCase().includes(term) ||
                q.description?.toLowerCase().includes(term)
            );
        }

        // Apply status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(q => q.status === filterStatus);
        }

        // Apply priority filter
        if (filterPriority !== 'all') {
            filtered = filtered.filter(q => q.priority === filterPriority);
        }

        // Apply category filter
        if (filterCategory !== 'all') {
            filtered = filtered.filter(q => q.category === filterCategory);
        }

        return filtered;
    };

    const getPaginatedMyQueries = () => {
        const filtered = getFilteredMyQueries();
        const pageSize = 5;
        const totalPages = Math.ceil(filtered.length / pageSize) || 1;
        const start = (myQueryPage - 1) * pageSize;
        const end = start + pageSize;
        const paginated = filtered.slice(start, end);

        return {
            data: paginated,
            totalItems: filtered.length,
            totalPages: totalPages,
            currentPage: myQueryPage,
            hasNext: myQueryPage < totalPages,
            hasPrevious: myQueryPage > 1
        };
    };

    // Render Comment Section Component for a specific query
    const renderCommentSection = (queryId) => {
        const queryComments = queryCommentsMap[queryId] || [];
        const isLoading = loadingComments[queryId];
        const isExpanded = expandedQueries.has(queryId);
        const commentValue = newCommentMap[queryId] || '';
        const attachmentValue = commentAttachmentMap[queryId] || null;

        if (!isExpanded) {
            return (
                <button
                    onClick={() => toggleExpandQuery(queryId)}
                    className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1 transition"
                >
                    <FiChevronDown size={16} />
                    Show Comments ({queryComments.length || 0})
                </button>
            );
        }

        return (
            <div className="mt-4 pt-3 border-t border-gray-200 animate-slide-down">
                <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <FiMessageSquare size={14} />
                        Comments ({queryComments.length})
                    </h5>
                    <button
                        onClick={() => toggleExpandQuery(queryId)}
                        className="text-sm text-gray-400 hover:text-gray-600 transition"
                    >
                        <FiChevronUp size={16} />
                    </button>
                </div>

                {/* Comments List */}
                <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : queryComments.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-2">No comments yet</p>
                    ) : (
                        queryComments.filter(c => !c.parent).map((comment) => (
                            <div key={comment.id} className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-xs flex-shrink-0">
                                        {comment.user?.full_name?.charAt(0) || 'U'}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between flex-wrap gap-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-sm text-gray-800">
                                                    {comment.user?.full_name || 'Unknown'}
                                                </span>
                                                {comment.is_hr && (
                                                    <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">
                                                        HR
                                                    </span>
                                                )}
                                                {comment.is_resolution && (
                                                    <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                                        Resolution
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-xs text-gray-400">
                                                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-700 mt-1">{comment.comment}</p>
                                        {comment.attachment_url && (
                                            <a
                                                href={comment.attachment_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-indigo-600 hover:underline flex items-center gap-1 mt-1"
                                            >
                                                <FiPaperclip size={12} />
                                                View Attachment
                                            </a>
                                        )}
                                        {isAdminOrHR && (
                                            <button
                                                onClick={() => {
                                                    deleteComment(comment.id);
                                                    // Refresh comments after deletion
                                                    setTimeout(() => {
                                                        fetchComments(queryId).then(updated => {
                                                            if (updated) {
                                                                setQueryCommentsMap(prev => ({ ...prev, [queryId]: updated }));
                                                            }
                                                        });
                                                    }, 300);
                                                }}
                                                className="text-xs text-red-500 hover:text-red-700 mt-1"
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Add Comment Input */}
                <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={commentValue}
                            onChange={(e) => setNewCommentMap(prev => ({ ...prev, [queryId]: e.target.value }))}
                            placeholder="Write a comment..."
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 text-sm"
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleAddComment(queryId);
                                }
                            }}
                        />
                        <label className="p-2 text-gray-400 hover:text-indigo-600 cursor-pointer rounded-lg hover:bg-indigo-50 transition flex-shrink-0">
                            <FiPaperclip size={18} />
                            <input
                                type="file"
                                className="hidden"
                                onChange={(e) => setCommentAttachmentMap(prev => ({ ...prev, [queryId]: e.target.files[0] }))}
                            />
                        </label>
                        <button
                            onClick={() => handleAddComment(queryId)}
                            disabled={!commentValue.trim() || isSubmitting}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50 flex items-center gap-2 text-sm flex-shrink-0"
                        >
                            <FiSend size={14} />
                            Send
                        </button>
                    </div>
                    {attachmentValue && (
                        <div className="text-xs text-gray-500 flex items-center gap-2">
                            <FiPaperclip size={12} />
                            {attachmentValue.name}
                            <button
                                onClick={() => setCommentAttachmentMap(prev => ({ ...prev, [queryId]: null }))}
                                className="text-red-500 hover:text-red-700"
                            >
                                <FiX size={14} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render Pagination Component
    const renderPagination = (currentPage, totalPages, onPageChange, totalItems, pageSize, label = 'items') => {
        if (totalPages <= 1) return null;

        return (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                    Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} {label}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg transition ${currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        <FiChevronLeft size={18} />
                    </button>

                    <div className="flex items-center gap-1">
                        {Array.from(
                            { length: Math.min(5, totalPages) },
                            (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }

                                if (pageNum < 1 || pageNum > totalPages) return null;

                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => onPageChange(pageNum)}
                                        className={`w-10 h-10 rounded-lg transition ${currentPage === pageNum
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            }
                        )}

                        {totalPages > 5 && currentPage < totalPages - 2 && (
                            <>
                                <span className="text-gray-400">...</span>
                                <button
                                    onClick={() => onPageChange(totalPages)}
                                    className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                                >
                                    {totalPages}
                                </button>
                            </>
                        )}
                    </div>

                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg transition ${currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        <FiChevronRight size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // ===================== RENDER =====================

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (filterTimeoutRef.current) {
                clearTimeout(filterTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="animate-fade-in pb-8">
            {/* Header */}
            <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
                            <FiMessageSquare className="text-indigo-600" />
                            {isAdminOrHR ? 'Employee Queries' : 'My Queries'}
                            {dashboardStats?.total > 0 && isAdminOrHR && (
                                <span className="text-sm font-normal text-gray-500">
                                    ({dashboardStats.total} total)
                                </span>
                            )}
                        </h1>
                        <p className="text-gray-500 mt-1">
                            {isAdminOrHR ? 'Manage and track employee queries' : 'Submit and track your queries'}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {isAdminOrHR && dashboardStats && (
                            <div className="flex items-center gap-2 text-sm bg-gray-100 px-3 py-1.5 rounded-xl">
                                <span className="text-red-500 font-medium">{dashboardStats.pending || 0}</span>
                                <span className="text-gray-500">pending</span>
                                <span className="text-gray-300">|</span>
                                <span className="text-yellow-500 font-medium">{dashboardStats.in_progress || 0}</span>
                                <span className="text-gray-500">in progress</span>
                            </div>
                        )}
                        <button
                            onClick={handleRefresh}
                            className="p-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition"
                            title="Refresh"
                        >
                            <FiRefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        </button>
                        {isEmployee && (
                            <button
                                onClick={() => setShowNewQueryModal(true)}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2 text-sm"
                            >
                                <FiPlus size={16} />
                                New Query
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-1 mb-6 flex flex-wrap">
                {isAdminOrHR && (
                    <>
                        <button
                            onClick={() => handleTabChange('groups')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${activeTab === 'groups'
                                ? 'bg-indigo-600 text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <FiUsers size={18} />
                            Employees
                            {employeePagination.totalItems > 0 && (
                                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === 'groups'
                                    ? 'bg-white text-indigo-600'
                                    : 'bg-indigo-100 text-indigo-600'
                                    }`}>
                                    {employeePagination.totalItems}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => handleTabChange('all')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${activeTab === 'all'
                                ? 'bg-indigo-600 text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <FiFolder size={18} />
                            All Queries
                            {pagination.totalItems > 0 && (
                                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === 'all'
                                    ? 'bg-white text-indigo-600'
                                    : 'bg-indigo-100 text-indigo-600'
                                    }`}>
                                    {pagination.totalItems}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => handleTabChange('stats')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${activeTab === 'stats'
                                ? 'bg-indigo-600 text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <FiBarChart2 size={18} />
                            Dashboard
                        </button>
                    </>
                )}
                {isEmployee && (
                    <button
                        onClick={() => handleTabChange('my')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${activeTab === 'my'
                            ? 'bg-indigo-600 text-white shadow-md'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <FiUser size={18} />
                        My Queries
                        {myQueries.length > 0 && (
                            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === 'my'
                                ? 'bg-white text-indigo-600'
                                : 'bg-indigo-100 text-indigo-600'
                                }`}>
                                {myQueries.length}
                            </span>
                        )}
                    </button>
                )}
            </div>

            {/* Search and Filters */}
            {activeTab !== 'stats' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder={isAdminOrHR ? "Search employees, queries, titles..." : "Search your queries..."}
                                value={searchTerm}
                                onChange={handleSearch}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
                            />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <select
                                value={filterStatus}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white text-sm"
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="in_progress">In Progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                                <option value="rejected">Rejected</option>
                            </select>
                            <select
                                value={filterPriority}
                                onChange={(e) => handleFilterChange('priority', e.target.value)}
                                className="px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white text-sm"
                            >
                                <option value="all">All Priority</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                            <select
                                value={filterCategory}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                                className="px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white text-sm"
                            >
                                <option value="all">All Categories</option>
                                <option value="hr">HR Related</option>
                                <option value="payroll">Payroll/Salary</option>
                                <option value="leave">Leave Management</option>
                                <option value="attendance">Attendance</option>
                                <option value="policy">Company Policy</option>
                                <option value="facility">Facility</option>
                                <option value="it">IT Support</option>
                                <option value="general">General</option>
                            </select>
                            {(searchTerm || filterStatus !== 'all' || filterPriority !== 'all' || filterCategory !== 'all') && (
                                <button
                                    onClick={clearFilters}
                                    className="px-3 py-2 text-gray-500 hover:text-gray-700 rounded-xl border border-gray-200 hover:bg-gray-50 transition text-sm flex items-center gap-1"
                                >
                                    <FiX size={14} /> Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3 text-red-700">
                        <FiAlertCircle size={20} />
                        <span>{error}</span>
                    </div>
                    <button onClick={clearError} className="text-red-500 hover:text-red-700">
                        <FiX size={18} />
                    </button>
                </div>
            )}

            {/* Loading State */}
            {loading && activeTab !== 'stats' && (
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                        <p className="text-gray-500">Loading queries...</p>
                    </div>
                </div>
            )}

            {/* Main Content - Groups Tab (HR/Admin) */}
            {activeTab === 'groups' && isAdminOrHR && !loading && (
                <div className="space-y-4">
                    {employeeGroups.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiUsers className="text-gray-400 text-2xl" />
                            </div>
                            <p className="text-gray-500">No employee queries found</p>
                            <p className="text-sm text-gray-400 mt-1">Employees haven't submitted any queries yet</p>
                        </div>
                    ) : (
                        <>
                            {employeeGroups.map((employee) => {
                                const employeeKey = `${employee.employee_id}-${employee.user_id}`;
                                const isExpanded = expandedEmployees.has(employeeKey);
                                const isAnimated = animatedEmployees.has(employeeKey);
                                const isHighlighted = highlightedEmployee === employeeKey;

                                const hasPending = employee.pending_count > 0;
                                const hasNewQuery = employee.latest_query &&
                                    new Date(employee.latest_query.created_at) > new Date(Date.now() - 30000);

                                return (
                                    <div
                                        key={employeeKey}
                                        className={`bg-white rounded-2xl shadow-sm border-2 overflow-hidden transition-all duration-500 
                                            ${isHighlighted ? 'border-green-400 shadow-lg shadow-green-100' :
                                                hasPending ? 'border-red-200' : 'border-gray-200'}
                                            ${isAnimated ? 'animate-slide-down' : ''}`}
                                    >
                                        {/* Employee Profile Header */}
                                        <div
                                            className={`p-4 cursor-pointer hover:bg-gray-50 transition-all duration-300 
                                                ${isHighlighted ? 'bg-green-50' : ''}`}
                                            onClick={() => toggleExpandEmployee(employeeKey)}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                                    <div className="relative flex-shrink-0">
                                                        {employee.profile_pic ? (
                                                            <img
                                                                src={employee.profile_pic}
                                                                alt={employee.name}
                                                                className="w-14 h-14 rounded-full object-cover border-2 border-gray-200"
                                                            />
                                                        ) : (
                                                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-xl">
                                                                {employee.name?.charAt(0) || 'E'}
                                                            </div>
                                                        )}
                                                        {hasNewQuery && (
                                                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                                                                <FiBell className="text-white text-xs" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-3 flex-wrap">
                                                            <h3 className="font-semibold text-gray-800 text-lg">
                                                                {employee.name}
                                                            </h3>
                                                            {hasNewQuery && (
                                                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-500 text-white animate-pulse">
                                                                    New Query
                                                                </span>
                                                            )}
                                                            {hasPending && (
                                                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                                                    {employee.pending_count} Pending
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 mt-0.5">
                                                            <span className="flex items-center gap-1">
                                                                <FiMail size={14} />
                                                                {employee.email}
                                                            </span>
                                                            <span>•</span>
                                                            <span className="flex items-center gap-1">
                                                                <FiUser size={14} />
                                                                ID: {employee.employee_id}
                                                            </span>
                                                            <span>•</span>
                                                            <span>{employee.department || 'N/A'}</span>
                                                            <span>•</span>
                                                            <span>{employee.position || 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-4 flex-shrink-0 ml-2">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <span className="flex items-center gap-1 text-green-600">
                                                            <FiCheckCircle size={14} />
                                                            {employee.resolved_count}
                                                        </span>
                                                        <span className="flex items-center gap-1 text-yellow-600">
                                                            <FiRefreshCw size={14} />
                                                            {employee.in_progress_count}
                                                        </span>
                                                        <span className="flex items-center gap-1 text-red-600">
                                                            <FiClock size={14} />
                                                            {employee.pending_count}
                                                        </span>
                                                    </div>
                                                    <span className={`text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                                                        <FiChevronDown size={20} />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="border-t border-gray-100 p-4 bg-gray-50">
                                                {employee.queries.length === 0 ? (
                                                    <p className="text-center text-gray-500 py-4">No queries from this employee</p>
                                                ) : (
                                                    <div className="space-y-4">
                                                        {employee.queries.map((query) => {
                                                            const statusInfo = getStatusBadge(query.status);
                                                            const StatusIcon = getStatusIcon(query.status);
                                                            const priorityInfo = getPriorityBadge(query.priority);
                                                            const isQueryExpanded = expandedQueries.has(query.id);

                                                            return (
                                                                <div
                                                                    key={query.id}
                                                                    className={`bg-white rounded-xl shadow-sm border-l-4 p-4 transition-all hover:shadow-md
                                                                        ${query.status === 'pending' ? 'border-red-400' :
                                                                            query.status === 'in_progress' ? 'border-yellow-400' :
                                                                                query.status === 'resolved' ? 'border-green-400' :
                                                                                    query.status === 'closed' ? 'border-blue-400' :
                                                                                        'border-gray-400'}`}
                                                                >
                                                                    <div className="flex items-start justify-between">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                                <div className="flex items-center gap-1">
                                                                                    <StatusIcon size={14} className={`text-${statusInfo.color}-500`} />
                                                                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${statusInfo.color}-100 text-${statusInfo.color}-700`}>
                                                                                        {statusInfo.label}
                                                                                    </span>
                                                                                </div>
                                                                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(query.priority)}`}>
                                                                                    {priorityInfo.label}
                                                                                </span>
                                                                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                                                                    {getCategoryLabel(query.category)}
                                                                                </span>
                                                                                {query.is_overdue && (
                                                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 animate-pulse">
                                                                                        Overdue
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <h4 className="font-medium text-gray-800 mt-1">
                                                                                {query.title}
                                                                            </h4>
                                                                            <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                                                                                {query.description}
                                                                            </p>
                                                                            <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                                                                                <span>{formatDateDisplay(query.created_at)}</span>
                                                                                <span>•</span>
                                                                                <span className="flex items-center gap-1">
                                                                                    <FiMessageSquare size={12} />
                                                                                    {query.total_comments || 0} comments
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    openQueryDetail(query.id);
                                                                                }}
                                                                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                                                                title="View Details"
                                                                            >
                                                                                <FiEye size={16} />
                                                                            </button>
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    openEditModal(query);
                                                                                }}
                                                                                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                                                                title="Edit"
                                                                            >
                                                                                <FiEdit2 size={16} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    {/* Comment Section with Expand/Collapse */}
                                                                    {renderCommentSection(query.id)}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {renderPagination(
                                employeePagination.currentPage,
                                employeePagination.totalPages,
                                handleEmployeePageChange,
                                employeePagination.totalItems,
                                employeePagination.pageSize || 5,
                                'employees'
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Main Content - All Queries Tab (HR/Admin) */}
            {activeTab === 'all' && isAdminOrHR && !loading && (
                <div className="space-y-3">
                    {queries.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiMessageSquare className="text-gray-400 text-2xl" />
                            </div>
                            <p className="text-gray-500">No queries found</p>
                            <p className="text-sm text-gray-400 mt-1">
                                {searchTerm || filterStatus !== 'all' || filterPriority !== 'all' || filterCategory !== 'all'
                                    ? 'No queries match your filters'
                                    : 'No queries have been submitted yet'}
                            </p>
                        </div>
                    ) : (
                        <>
                            {queries.map((query) => (
                                <div key={query.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${getStatusBadge(query.status).color}-100 text-${getStatusBadge(query.status).color}-700`}>
                                                    {getStatusBadge(query.status).label}
                                                </span>
                                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(query.priority)}`}>
                                                    {getPriorityBadge(query.priority).label}
                                                </span>
                                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                                    {getCategoryLabel(query.category)}
                                                </span>
                                                {query.is_overdue && (
                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 animate-pulse">
                                                        Overdue
                                                    </span>
                                                )}
                                            </div>
                                            <h4 className="font-medium text-gray-800 mt-1">{query.title}</h4>
                                            <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{query.description}</p>
                                            <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                                                <span className="flex items-center gap-1">
                                                    <FiUser size={12} />
                                                    {query.employee?.full_name}
                                                </span>
                                                <span>•</span>
                                                <span>{formatDateDisplay(query.created_at)}</span>
                                                <span>•</span>
                                                <span className="flex items-center gap-1">
                                                    <FiMessageSquare size={12} />
                                                    {query.total_comments || 0} comments
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                            <button
                                                onClick={() => openQueryDetail(query.id)}
                                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                            >
                                                <FiEye size={16} />
                                            </button>
                                            <button
                                                onClick={() => openEditModal(query)}
                                                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                            >
                                                <FiEdit2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    {/* Comment Section with Expand/Collapse */}
                                    {renderCommentSection(query.id)}
                                </div>
                            ))}

                            {renderPagination(
                                pagination.currentPage || queryPage,
                                pagination.totalPages || 1,
                                handleQueryPageChange,
                                pagination.totalItems || queries.length,
                                pagination.pageSize || 10,
                                'queries'
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Main Content - Stats Tab (HR/Admin) */}
            {activeTab === 'stats' && isAdminOrHR && dashboardStats && (
                <div className="space-y-6">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[
                            { label: 'Total', value: dashboardStats.total, icon: FiFolder, color: 'indigo' },
                            { label: 'Pending', value: dashboardStats.pending, icon: FiClock, color: 'red' },
                            { label: 'In Progress', value: dashboardStats.in_progress, icon: FiRefreshCw, color: 'yellow' },
                            { label: 'Resolved', value: dashboardStats.resolved, icon: FiCheckCircle, color: 'green' },
                        ].map(stat => (
                            <div key={stat.label} className="stat-card bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500">{stat.label}</p>
                                        <p className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</p>
                                    </div>
                                    <div className={`w-10 h-10 bg-${stat.color}-100 rounded-xl flex items-center justify-center text-${stat.color}-600`}>
                                        <stat.icon size={20} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Category Stats */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <FiTag className="text-indigo-600" />
                            By Category
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {Object.entries(dashboardStats.by_category || {}).map(([category, count]) => (
                                <div key={category} className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
                                    <span className="text-sm text-gray-600">{getCategoryLabel(category)}</span>
                                    <span className="font-semibold text-gray-800">{count}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Priority Stats */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <FiAlertCircle className="text-indigo-600" />
                            By Priority
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {Object.entries(dashboardStats.by_priority || {}).map(([priority, count]) => (
                                <div key={priority} className={`rounded-xl p-3 flex items-center justify-between ${getPriorityColor(priority)}`}>
                                    <span className="text-sm font-medium capitalize">{priority}</span>
                                    <span className="font-semibold">{count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content - My Queries Tab (Employee) */}
            {activeTab === 'my' && isEmployee && !loading && (
                <div className="space-y-3">
                    {(() => {
                        const { data: paginatedData, totalItems, totalPages, currentPage } = getPaginatedMyQueries();

                        if (totalItems === 0) {
                            return (
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <FiMessageSquare className="text-gray-400 text-2xl" />
                                    </div>
                                    <p className="text-gray-500">No queries found</p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        {searchTerm || filterStatus !== 'all' || filterPriority !== 'all' || filterCategory !== 'all'
                                            ? 'No queries match your filters'
                                            : 'Submit your first query to get started'}
                                    </p>
                                    {!searchTerm && filterStatus === 'all' && filterPriority === 'all' && filterCategory === 'all' && (
                                        <button
                                            onClick={() => setShowNewQueryModal(true)}
                                            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2 mx-auto"
                                        >
                                            <FiPlus size={16} />
                                            Create New Query
                                        </button>
                                    )}
                                </div>
                            );
                        }

                        return (
                            <>
                                {paginatedData.map((query) => (
                                    <div key={query.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${getStatusBadge(query.status).color}-100 text-${getStatusBadge(query.status).color}-700`}>
                                                        {getStatusBadge(query.status).label}
                                                    </span>
                                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(query.priority)}`}>
                                                        {getPriorityBadge(query.priority).label}
                                                    </span>
                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                                        {getCategoryLabel(query.category)}
                                                    </span>
                                                    {query.is_overdue && (
                                                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 animate-pulse">
                                                            Overdue
                                                        </span>
                                                    )}
                                                </div>
                                                <h4 className="font-medium text-gray-800 mt-1">{query.title}</h4>
                                                <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{query.description}</p>
                                                <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                                                    <span>{formatDateDisplay(query.created_at)}</span>
                                                    <span>•</span>
                                                    <span className="flex items-center gap-1">
                                                        <FiMessageSquare size={12} />
                                                        {query.total_comments || 0} comments
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                                <button
                                                    onClick={() => openQueryDetail(query.id)}
                                                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                                >
                                                    <FiEye size={16} />
                                                </button>
                                                <button
                                                    onClick={() => openEditModal(query)}
                                                    className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                                >
                                                    <FiEdit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteQuery(query.id)}
                                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                                >
                                                    <FiTrash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                        {/* Comment Section with Expand/Collapse */}
                                        {renderCommentSection(query.id)}
                                    </div>
                                ))}

                                {renderPagination(
                                    currentPage,
                                    totalPages,
                                    (page) => setMyQueryPage(page),
                                    totalItems,
                                    5,
                                    'queries'
                                )}
                            </>
                        );
                    })()}
                </div>
            )}

            {/* New Query Modal */}
            {showNewQueryModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowNewQueryModal(false)} />
                    <div className="relative min-h-screen flex items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white z-10 p-6 border-b border-gray-200 flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    <FiPlus className="text-indigo-600" />
                                    New Query
                                </h3>
                                <button onClick={() => setShowNewQueryModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                                    <FiX size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmitQuery} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleFormChange}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                        placeholder="Brief summary of your issue"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleFormChange}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                        placeholder="Describe your issue in detail"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Category
                                        </label>
                                        <select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleFormChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
                                        >
                                            <option value="hr">HR Related</option>
                                            <option value="payroll">Payroll/Salary</option>
                                            <option value="leave">Leave Management</option>
                                            <option value="attendance">Attendance</option>
                                            <option value="policy">Company Policy</option>
                                            <option value="facility">Facility/Infrastructure</option>
                                            <option value="it">IT Support</option>
                                            <option value="general">General</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Priority
                                        </label>
                                        <select
                                            name="priority"
                                            value={formData.priority}
                                            onChange={handleFormChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
                                        >
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Attachment
                                    </label>
                                    <input
                                        type="file"
                                        name="attachment"
                                        onChange={handleFormChange}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                    />
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        name="is_public"
                                        checked={formData.is_public}
                                        onChange={(e) => setFormData(prev => ({ ...prev, is_public: e.target.checked }))}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <label className="text-sm text-gray-700">Make this query public</label>
                                </div>

                                <div className="flex gap-3 pt-4 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => setShowNewQueryModal(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50 flex items-center gap-2"
                                    >
                                        <FiSend size={16} />
                                        {isSubmitting ? 'Submitting...' : 'Submit Query'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Query Modal */}
            {showEditModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowEditModal(false)} />
                    <div className="relative min-h-screen flex items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white z-10 p-6 border-b border-gray-200 flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    <FiEdit2 className="text-green-600" />
                                    Edit Query
                                </h3>
                                <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                                    <FiX size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleUpdateQuery} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={editFormData.title || ''}
                                        onChange={handleEditFormChange}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea
                                        name="description"
                                        value={editFormData.description || ''}
                                        onChange={handleEditFormChange}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                        <select
                                            name="category"
                                            value={editFormData.category || 'general'}
                                            onChange={handleEditFormChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
                                        >
                                            <option value="hr">HR Related</option>
                                            <option value="payroll">Payroll/Salary</option>
                                            <option value="leave">Leave Management</option>
                                            <option value="attendance">Attendance</option>
                                            <option value="policy">Company Policy</option>
                                            <option value="facility">Facility/Infrastructure</option>
                                            <option value="it">IT Support</option>
                                            <option value="general">General</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                                        <select
                                            name="priority"
                                            value={editFormData.priority || 'medium'}
                                            onChange={handleEditFormChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
                                        >
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>
                                </div>

                                {isAdminOrHR && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                            <select
                                                name="status"
                                                value={editFormData.status || 'pending'}
                                                onChange={handleEditFormChange}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="in_progress">In Progress</option>
                                                <option value="resolved">Resolved</option>
                                                <option value="closed">Closed</option>
                                                <option value="rejected">Rejected</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Resolution Notes</label>
                                            <textarea
                                                name="resolution_notes"
                                                value={editFormData.resolution_notes || ''}
                                                onChange={handleEditFormChange}
                                                rows={2}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                                placeholder="Add resolution notes..."
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="flex gap-3 pt-4 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => setShowEditModal(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-2"
                                    >
                                        <FiSave size={16} />
                                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail Modal */}
            {showDetailModal && selectedQuery && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowDetailModal(false)} />
                    <div className="relative min-h-screen flex items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white z-10 p-6 border-b border-gray-200 flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    <FiFileText className="text-indigo-600" />
                                    Query Details
                                </h3>
                                <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                                    <FiX size={20} />
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Query Info */}
                                <div className="bg-gray-50 rounded-xl p-4">
                                    <h4 className="text-lg font-semibold text-gray-800">{selectedQuery.title}</h4>
                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getStatusBadge(selectedQuery.status).color}-100 text-${getStatusBadge(selectedQuery.status).color}-700`}>
                                            {getStatusBadge(selectedQuery.status).label}
                                        </span>
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(selectedQuery.priority)}`}>
                                            {getPriorityBadge(selectedQuery.priority).label}
                                        </span>
                                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                            {getCategoryLabel(selectedQuery.category)}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap">{selectedQuery.description}</p>
                                    {selectedQuery.attachment_url && (
                                        <div className="mt-2 flex items-center gap-2 text-sm">
                                            <FiPaperclip className="text-gray-400" />
                                            <a href={selectedQuery.attachment_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                                                {selectedQuery.attachment_name || 'View Attachment'}
                                            </a>
                                        </div>
                                    )}
                                </div>

                                {/* Meta Info */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-gray-50 rounded-xl p-3">
                                        <p className="text-xs text-gray-500">Employee</p>
                                        <p className="text-sm font-medium">{selectedQuery.employee?.full_name || 'Unknown'}</p>
                                        <p className="text-xs text-gray-400">{selectedQuery.employee?.employee_id || ''}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-3">
                                        <p className="text-xs text-gray-500">Assigned To</p>
                                        <p className="text-sm font-medium">{selectedQuery.assigned_to?.full_name || 'Unassigned'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-3">
                                        <p className="text-xs text-gray-500">Created</p>
                                        <p className="text-sm font-medium">{formatDateDisplay(selectedQuery.created_at)}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-3">
                                        <p className="text-xs text-gray-500">Last Updated</p>
                                        <p className="text-sm font-medium">{formatDateDisplay(selectedQuery.updated_at)}</p>
                                    </div>
                                </div>

                                {/* Comments */}
                                <div>
                                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                        <FiMessageSquare size={16} />
                                        Comments ({comments.length})
                                    </h4>
                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                        {comments.length === 0 ? (
                                            <p className="text-sm text-gray-400">No comments yet</p>
                                        ) : (
                                            comments.filter(c => !c.parent).map((comment) => (
                                                <div key={comment.id} className="bg-gray-50 rounded-xl p-3">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-xs flex-shrink-0">
                                                            {comment.user?.full_name?.charAt(0) || 'U'}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-medium text-sm">{comment.user?.full_name}</span>
                                                                    {comment.is_hr && (
                                                                        <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">HR</span>
                                                                    )}
                                                                    {comment.is_resolution && (
                                                                        <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">Resolution</span>
                                                                    )}
                                                                </div>
                                                                <span className="text-xs text-gray-400">
                                                                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-700 mt-1">{comment.comment}</p>
                                                            {comment.attachment_url && (
                                                                <a href={comment.attachment_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline flex items-center gap-1 mt-1">
                                                                    <FiPaperclip size={12} /> View Attachment
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 flex justify-end gap-3">
                                <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition">
                                    Close
                                </button>
                                {isAdminOrHR && (
                                    <button
                                        onClick={() => {
                                            setShowDetailModal(false);
                                            openEditModal(selectedQuery);
                                        }}
                                        className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition flex items-center gap-2"
                                    >
                                        <FiEdit2 size={16} /> Edit
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CSS Animations */}
            <style jsx>{`
                @keyframes slide-down {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-slide-down {
                    animation: slide-down 0.3s ease-out;
                }
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out;
                }
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `}</style>
        </div>
    );
};

export default EmployeeQuery;